import { confirmed as passwordChangedNotice } from '../../../../shared/password-change';
import React from 'react';
import { connect } from 'react-redux';
import { translate } from '../../base/i18n/functions';
import SettingsButton from '../../settings/components/web/SettingsButton';
import { SETTINGS_TABS } from '../../settings/constants';
import CertifiedIpCtsNotice from '../../base/certified-mode/components/CertifiedIpCtsNotice.web';
import { AbstractWelcomePage, _mapStateToProps } from './AbstractWelcomePage';
import { consumePendingRoomRedirect, setPersistentItem } from '../../vrs-auth/storage';
import { releaseBrowserMediaDevices } from '../../vrs-layout/mediaCleanup.web';
import { getAppName, getLogoUrl, getLogoWhiteUrl, getWhitelabelConfig, isFeatureEnabled } from '../../base/whitelabel/functions';
function getInitialSelectedTab() {
    if (hasInterpreterAccess()) {
        return 'interpreter';
    }
    return 'captioner';
}
function hasInterpreterAccess() {
    return isFeatureEnabled('vrs') || isFeatureEnabled('vri');
}
/**
 * The Web container rendering the welcome page — a single centered auth box
 * with Interpreter / Captioner tabs that shift the entire background, plus a
 * signpost to the separate client portal.
 */
class WelcomePage extends AbstractWelcomePage {
    constructor(props) {
        super(props);
        this.state = {
            ...this.state,
            generateRoomNames: interfaceConfig.GENERATE_ROOMNAMES_ON_WELCOME_PAGE,
            recentMeetingsCollapsed: true,
            selectedTab: getInitialSelectedTab(),
            email: '',
            password: '',
            showPassword: false,
            isSubmitting: false,
            // Set once a password has been accepted and a second factor is
            // outstanding. The challenge id stands in for the password, which
            // is therefore never held for a second request.
            challengeId: '',
            phoneHint: '',
            twoFactorCode: '',
            error: ''
        };
        this._titleHasNotAllowCharacter = false;
        this._additionalContentRef = null;
        this._roomInputRef = null;
        this._additionalToolbarContentRef = null;
        this._additionalCardRef = null;
        this._emailRef = null;
        this._passwordRef = null;
        this._additionalCardTemplate = document.getElementById('welcome-page-additional-card-template');
        this._additionalContentTemplate = document.getElementById('welcome-page-additional-content-template');
        this._additionalToolbarContentTemplate = document.getElementById('settings-toolbar-additional-content-template');
        this._onFormSubmit = this._onFormSubmit.bind(this);
        this._onRoomChange = this._onRoomChange.bind(this);
        this._setAdditionalCardRef = this._setAdditionalCardRef.bind(this);
        this._setAdditionalContentRef = this._setAdditionalContentRef.bind(this);
        this._setRoomInputRef = this._setRoomInputRef.bind(this);
        this._setAdditionalToolbarContentRef = this._setAdditionalToolbarContentRef.bind(this);
        this._renderFooter = this._renderFooter.bind(this);
        this._handleTabSwitch = this._handleTabSwitch.bind(this);
        this._handleAuthSubmit = this._handleAuthSubmit.bind(this);
        this._handleEmailChange = this._handleEmailChange.bind(this);
        this._handlePasswordChange = this._handlePasswordChange.bind(this);
        this._togglePasswordVisibility = this._togglePasswordVisibility.bind(this);
        this._handleTwoFactorCodeChange = this._handleTwoFactorCodeChange.bind(this);
        this._handleTwoFactorSubmit = this._handleTwoFactorSubmit.bind(this);
        this._goToPasswordReset = this._goToPasswordReset.bind(this);
    }
    componentDidMount() {
        super.componentDidMount();
        releaseBrowserMediaDevices();
        document.body.classList.add('welcome-page');
        document.title = interfaceConfig.APP_NAME;
        if (this.state.generateRoomNames) {
            this._updateRoomName();
        }
        if (this._shouldShowAdditionalContent()) {
            this._additionalContentRef?.appendChild(this._additionalContentTemplate?.content.cloneNode(true));
        }
        if (this._shouldShowAdditionalToolbarContent()) {
            this._additionalToolbarContentRef?.appendChild(this._additionalToolbarContentTemplate?.content.cloneNode(true));
        }
        if (this._shouldShowAdditionalCard()) {
            this._additionalCardRef?.appendChild(this._additionalCardTemplate?.content.cloneNode(true));
        }
        this._applyTheme();
    }
    componentDidUpdate(prevProps, prevState) {
        if (prevState.selectedTab !== this.state.selectedTab) {
            this._applyTheme();
        }
    }
    componentWillUnmount() {
        super.componentWillUnmount();
        document.body.classList.remove('welcome-page');
        document.body.style.removeProperty('--vrs-bg');
        document.body.style.removeProperty('--vrs-accent');
    }
    /**
     * Apply background and accent theme based on selected tab.
     */
    _applyTheme() {
        const tab = this.state.selectedTab;
        if (tab === 'interpreter') {
            document.body.style.setProperty('--vrs-bg', 'linear-gradient(135deg, #0f1724 0%, #1a2332 50%, #252f3f 100%)');
            document.body.style.setProperty('--vrs-accent', '#7eb8da');
        }
        else {
            document.body.style.setProperty('--vrs-bg', 'linear-gradient(135deg, #2b1b0f 0%, #5a2f14 48%, #9a631c 100%)');
            document.body.style.setProperty('--vrs-accent', '#f2b94b');
        }
    }
    /**
     * Switch between role tabs.
     */
    _handleTabSwitch(tab) {
        this.setState({
            selectedTab: tab,
            error: ''
        });
    }
    _handleEmailChange(e) {
        this.setState({ email: e.target.value, error: '' });
    }
    _handlePasswordChange(e) {
        this.setState({ password: e.target.value, error: '' });
    }
    _handleTwoFactorCodeChange(e) {
        this.setState({ twoFactorCode: e.target.value, error: '' });
    }
    /**
     * Completes a sign-in that stopped at the second factor.
     */
    async _handleTwoFactorSubmit(e) {
        e.preventDefault();
        const { challengeId, twoFactorCode } = this.state;
        if (!twoFactorCode) {
            this.setState({ error: 'Enter the code we sent you.' });
            return;
        }
        this.setState({ isSubmitting: true, error: '' });
        try {
            const res = await fetch(`${this._getApiBase()}/api/auth/2fa/challenge`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ challengeId, code: twoFactorCode })
            });
            const data = await res.json();
            if (!res.ok) {
                this.setState({
                    error: data.error || 'That code is not valid.',
                    isSubmitting: false
                });
                return;
            }
            this._completeSignIn(data, data.user?.role || this.state.selectedTab);
        }
        catch (err) {
            this.setState({
                error: 'Network error. Please check your connection.',
                isSubmitting: false
            });
        }
    }
    _togglePasswordVisibility() {
        this.setState((prev) => ({
            showPassword: !prev.showPassword
        }));
    }
    /**
     * Password reset is not a client flow. This used to send an interpreter or
     * a captioner to /client-profile.html, which cannot complete a reset for a
     * staff account — so "Forgot password?" led somewhere that could not help.
     */
    _goToPasswordReset() {
        const params = new URLSearchParams();
        const email = String(this.state.email || '').trim();
        if (email) {
            params.set('email', email);
        }
        const query = params.toString();
        window.location.href = `/reset-password.html${query ? `?${query}` : ''}`;
    }
    /**
     * Handle sign-in or registration form submission.
     */
    async _handleAuthSubmit(e) {
        e.preventDefault();
        const { selectedTab, email, password } = this.state;
        if (!email || !password) {
            this.setState({ error: 'Email and password are required.' });
            return;
        }
        this.setState({ isSubmitting: true, error: '' });
        // Staff accounts are created by administrators, so this portal only
        // ever signs in. Client self-registration lives on the client portal.
        const role = selectedTab;
        const apiBase = this._getApiBase();
        try {
            const res = await fetch(`${apiBase}/api/auth/${role}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });
            const data = await res.json();
            // A correct password is not a session when the account carries a
            // second factor: the server sends a code and hands back a
            // challenge instead of tokens.
            if (res.ok && data.twoFactorRequired) {
                this.setState({
                    challengeId: data.challengeId,
                    isSubmitting: false,
                    phoneHint: data.phoneHint || '',
                    twoFactorCode: '',
                    error: ''
                });
                return;
            }
            if (!res.ok) {
                this.setState({
                    error: data.error || 'Authentication failed.',
                    isSubmitting: false
                });
                return;
            }
            this._completeSignIn(data, role);
        }
        catch (err) {
            this.setState({
                error: 'Network error. Please check your connection.',
                isSubmitting: false
            });
        }
    }
    /**
     * Stores the session and leaves the portal.
     *
     * Shared by the plain password path and the two-factor path, so a session
     * established by either is stored identically — including the refresh
     * token, without which the profile surfaces sign the user out at the first
     * access-token expiry.
     */
    _completeSignIn(data, role) {
        setPersistentItem('vrs_auth_token', JSON.stringify({
            token: data.token,
            userId: data.user?.id,
            name: data.user?.name
        }));
        if (data.refreshToken) {
            setPersistentItem('vrs_refresh_token', data.refreshToken);
        }
        setPersistentItem('vrs_user_info', JSON.stringify(data.user));
        setPersistentItem('vrs_user_role', role);
        const pendingRoomRedirect = consumePendingRoomRedirect();
        if (pendingRoomRedirect) {
            window.location.href = pendingRoomRedirect;
        }
        else if (role === 'captioner') {
            window.location.href = '/captioner-profile.html';
        }
        else {
            window.location.href = '/interpreter-profile.html';
        }
    }
    _getApiBase() {
        if (typeof config !== 'undefined' && config.vrs?.queueServiceUrl) {
            const wsUrl = config.vrs.queueServiceUrl;
            return wsUrl.replace(/^ws/, 'http').replace(/\/ws$/, '');
        }
        if (typeof window !== 'undefined') {
            return window.location.origin;
        }
        return 'http://localhost:3001';
    }
    render() {
        const { t } = this.props;
        const { DEFAULT_WELCOME_PAGE_LOGO_URL, DISPLAY_WELCOME_FOOTER } = interfaceConfig;
        const showAdditionalCard = this._shouldShowAdditionalCard();
        const showAdditionalContent = this._shouldShowAdditionalContent();
        const showAdditionalToolbarContent = this._shouldShowAdditionalToolbarContent();
        const contentClassName = showAdditionalContent ? 'with-content' : 'without-content';
        const footerClassName = DISPLAY_WELCOME_FOOTER ? 'with-footer' : 'without-footer';
        const { selectedTab, email, password, showPassword, isSubmitting, error } = this.state;
        const { challengeId, phoneHint, twoFactorCode } = this.state;
        const awaitingSecondFactor = Boolean(challengeId);
        const isInterpreter = selectedTab === 'interpreter';
        const isCaptioner = selectedTab === 'captioner';
        const showInterpreterTab = hasInterpreterAccess();
        const themeClass = isInterpreter ? 'moon' : 'sun';
        const tenantId = getWhitelabelConfig()?.tenantId || 'malka';
        const tenantClass = `tenant-${tenantId}`;
        const logoUrl = tenantId === 'maple'
            ? 'images/maple-logo-original.png'
            : isInterpreter ? getLogoWhiteUrl() : getLogoUrl();
        const tabIndicatorClass = isInterpreter ? 'left' : 'right';
        // Rendered into a visually-hidden h1: the page needs a heading in its
        // outline, but not a second label on screen under a tab that says it.
        const authTitle = isInterpreter ? 'Interpreter queue' : 'Captioner console';
        return (React.createElement("div", { className: `welcome vrs-auth-page ${contentClassName} ${footerClassName}`, id: 'welcome_page' },
            React.createElement("div", { className: `vrs-auth-fullscreen ${themeClass} ${tenantClass}` },
                React.createElement("div", { "aria-hidden": 'true', className: 'vrs-celestial-background' },
                    React.createElement("div", { className: 'vrs-starfield vrs-starfield--near' }),
                    React.createElement("div", { className: 'vrs-starfield vrs-starfield--far' }),
                    React.createElement("div", { className: 'vrs-orbit-ring vrs-orbit-ring--wide' }),
                    React.createElement("div", { className: 'vrs-orbit-ring vrs-orbit-ring--tight' }),
                    React.createElement("div", { className: 'vrs-auth-planet vrs-auth-planet--earth' },
                        React.createElement("span", { className: 'vrs-auth-planet-shade' })),
                    React.createElement("div", { className: 'vrs-auth-planet vrs-auth-planet--moon' },
                        React.createElement("span", { className: 'vrs-auth-crater vrs-auth-crater--one' }),
                        React.createElement("span", { className: 'vrs-auth-crater vrs-auth-crater--two' }),
                        React.createElement("span", { className: 'vrs-auth-crater vrs-auth-crater--three' })),
                    React.createElement("div", { className: 'vrs-auth-planet vrs-auth-planet--sun' })),
                React.createElement("div", { className: 'welcome-page-settings' },
                    React.createElement(SettingsButton, { defaultTab: SETTINGS_TABS.CALENDAR, isDisplayedOnWelcomePage: true }),
                    showAdditionalToolbarContent
                        ? React.createElement("div", { className: 'settings-toolbar-content', ref: this._setAdditionalToolbarContentRef })
                        : null),
                React.createElement("div", { className: 'vrs-auth-card' },
                    React.createElement("div", { className: 'vrs-auth-logo' },
                        React.createElement("img", { alt: getAppName(), className: 'vrs-auth-logo-img', src: logoUrl })),
                    React.createElement("div", { className: 'vrs-auth-tabs vrs-auth-tabs--two-up' },
                        React.createElement("div", { className: `vrs-auth-tab-indicator ${tabIndicatorClass}` }),
                        showInterpreterTab && (React.createElement("button", { className: `vrs-auth-tab ${isInterpreter ? 'active' : ''}`, onClick: () => this._handleTabSwitch('interpreter'), type: 'button' }, "Interpreter")),
                        React.createElement("button", { className: `vrs-auth-tab ${isCaptioner ? 'active' : ''}`, onClick: () => this._handleTabSwitch('captioner'), type: 'button' }, "Captioner")),
                    React.createElement("h1", { className: 'vrs-auth-title vrs-auth-title--visually-hidden' }, authTitle),
                    error && (React.createElement("div", { className: 'vrs-auth-error' }, error)),
                    awaitingSecondFactor ? (React.createElement("form", { className: 'vrs-auth-form', onSubmit: this._handleTwoFactorSubmit },
                        React.createElement("div", { className: 'vrs-auth-field' },
                            React.createElement("label", { htmlFor: 'vrs-2fa-code' }, phoneHint
                                ? `Code sent to ${phoneHint}`
                                : 'Verification code'),
                            React.createElement("input", { autoComplete: 'one-time-code', autoFocus: true, disabled: isSubmitting, id: 'vrs-2fa-code', inputMode: 'numeric', onChange: this._handleTwoFactorCodeChange, placeholder: '000000', required: true, type: 'text', value: twoFactorCode })),
                        React.createElement("button", { className: `vrs-auth-submit ${themeClass}`, disabled: isSubmitting, type: 'submit' }, isSubmitting ? 'Verifying...' : 'Verify'),
                        React.createElement("p", { className: 'vrs-auth-subtitle' }, "Lost your phone? Enter one of your recovery codes instead."))) : (React.createElement("form", { className: 'vrs-auth-form', onSubmit: this._handleAuthSubmit },
                        new URLSearchParams(window.location.search).get('password-changed') === '1' && (React.createElement("p", { className: 'vrs-auth-subtitle', role: 'status' }, passwordChangedNotice)),
                        React.createElement("div", { className: 'vrs-auth-field' },
                            React.createElement("label", { htmlFor: 'vrs-email' }, "Email"),
                            React.createElement("input", { autoComplete: 'email', disabled: isSubmitting, id: 'vrs-email', onChange: this._handleEmailChange, placeholder: 'you@example.com', required: true, type: 'email', value: email })),
                        React.createElement("div", { className: 'vrs-auth-field' },
                            React.createElement("label", { htmlFor: 'vrs-password' }, "Password"),
                            React.createElement("div", { className: 'vrs-password-control' },
                                React.createElement("input", { autoComplete: 'current-password', disabled: isSubmitting, id: 'vrs-password', onChange: this._handlePasswordChange, placeholder: '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022', required: true, type: showPassword ? 'text' : 'password', value: password }),
                                React.createElement("button", { "aria-label": showPassword ? 'Hide password' : 'Show password', "aria-pressed": showPassword, className: 'vrs-password-toggle', disabled: isSubmitting, onClick: this._togglePasswordVisibility, type: 'button' }, showPassword ? 'Hide' : 'Show'))),
                        React.createElement("button", { className: `vrs-auth-submit ${themeClass}`, disabled: isSubmitting, type: 'submit' }, isSubmitting ? 'Signing in...' : 'Sign In'))),
                    React.createElement("div", { className: 'vrs-auth-switch' },
                        React.createElement("button", { onClick: this._goToPasswordReset, type: 'button' }, "Forgot password?"),
                        React.createElement("span", null, "Need access? Ask an administrator to create your account.")),
                    React.createElement(CertifiedIpCtsNotice, null))),
            React.createElement("div", { style: { display: 'none' } },
                React.createElement("div", { className: 'welcome-page-content', ref: this._setAdditionalContentRef }),
                React.createElement("div", { className: 'welcome-card welcome-card--dark', ref: this._setAdditionalCardRef })),
            DISPLAY_WELCOME_FOOTER && this._renderFooter()));
    }
    /**
     * Prevents submission of the form and delegates join logic.
     */
    _onFormSubmit(event) {
        event.preventDefault();
        if (!this._roomInputRef || this._roomInputRef.reportValidity()) {
            this._onJoin();
        }
    }
    // @ts-ignore
    _onRoomChange(event) {
        const specialCharacters = ['?', '&', ':', '\'', '"', '%', '#', '.'];
        this._titleHasNotAllowCharacter = specialCharacters.some(char => event.target.value.includes(char));
        super._onRoomChange(event.target.value);
    }
    _renderFooter() {
        const { t } = this.props;
        return (React.createElement("footer", { className: 'welcome-footer' },
            React.createElement("div", { className: 'welcome-footer-centered' },
                React.createElement("div", { className: 'welcome-footer-padded' },
                    React.createElement("div", { className: 'welcome-footer-row-block welcome-footer--row-1' },
                        React.createElement("div", { className: 'welcome-footer-row-1-text' }, t('welcomepage.jitsiOnMobile')),
                        React.createElement("a", { className: 'welcome-badge', href: '#', onClick: e => e.preventDefault() },
                            React.createElement("img", { alt: t('welcomepage.mobileDownLoadLinkIos'), src: './images/app-store-badge.png' })),
                        React.createElement("a", { className: 'welcome-badge', href: '#', onClick: e => e.preventDefault() },
                            React.createElement("img", { alt: t('welcomepage.mobileDownLoadLinkAndroid'), src: './images/google-play-badge.png' })))))));
    }
    _setAdditionalCardRef(el) {
        this._additionalCardRef = el;
    }
    _setAdditionalContentRef(el) {
        this._additionalContentRef = el;
    }
    _setAdditionalToolbarContentRef(el) {
        this._additionalToolbarContentRef = el;
    }
    _setRoomInputRef(el) {
        this._roomInputRef = el;
    }
    _shouldShowAdditionalCard() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_ADDITIONAL_CARD
            && this._additionalCardTemplate
            && this._additionalCardTemplate.content
            && this._additionalCardTemplate.innerHTML.trim();
    }
    _shouldShowAdditionalContent() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_CONTENT
            && this._additionalContentTemplate
            && this._additionalContentTemplate.content
            && this._additionalContentTemplate.innerHTML.trim();
    }
    _shouldShowAdditionalToolbarContent() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_TOOLBAR_ADDITIONAL_CONTENT
            && this._additionalToolbarContentTemplate
            && this._additionalToolbarContentTemplate.content
            && this._additionalToolbarContentTemplate.innerHTML.trim();
    }
}
WelcomePage.defaultProps = {
    _room: ''
};
export default translate(connect(_mapStateToProps)(WelcomePage));
